// Importing a list of members into a department, one person at a time through the same function the
// Add member form uses (addMemberToDepartment). That keeps phone matching identical: a number already
// known to the church adds the EXISTING person to this department instead of creating a duplicate.
// The rows come from planImport (src/lib/memberImport.ts), which has already checked them.
import type { ImportRow } from '@/lib/memberImport'
import { addMemberToDepartment } from './members'

export type ImportResult = 'created' | 'added-existing' | 'already-member' | 'failed' | 'not-tried'

export interface ImportOutcome {
  line: number
  name: string
  result: ImportResult
  /** Why it failed or was not tried, in plain words. */
  message?: string
}

/** After this many failures in a row (usually the connection is down) the rest are not attempted. */
export const GIVE_UP_AFTER = 3

const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : 'Could not save this person.')

/**
 * Import the rows in order. One person failing never stops the others, and nothing is half-saved per
 * person that a re-run could not fix: running the same file again skips everyone already added.
 */
export async function importMembers(
  departmentId: string,
  rows: ImportRow[],
  onProgress?: (done: number, total: number) => void,
): Promise<ImportOutcome[]> {
  const out: ImportOutcome[] = []
  let failures = 0
  for (const row of rows) {
    if (failures >= GIVE_UP_AFTER) {
      out.push({ line: row.line, name: row.fullName, result: 'not-tried', message: 'Not tried: the connection or the server kept failing. Import the same file again; people already added are skipped.' })
    } else {
      try {
        const r = await addMemberToDepartment({
          departmentId,
          fullName: row.fullName,
          phone: row.phone,
          email: row.email,
          notes: row.notes,
          joinedOn: row.joinedOn,
        })
        failures = 0
        out.push({ line: row.line, name: row.fullName, result: r.kind })
      } catch (e) {
        failures += 1
        out.push({ line: row.line, name: row.fullName, result: 'failed', message: errText(e) })
      }
    }
    onProgress?.(out.length, rows.length)
  }
  return out
}
